import React from "react";
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
    Tooltip,
    Typography,
    CircularProgress,
    Autocomplete,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
    CUSTOMER_SELECT_EMPTY_HELPER,
    CUSTOMER_SELECT_LABEL,
    CUSTOMER_SELECT_LOADING,
    CUSTOMER_SELECT_NO_OPTIONS,
    CUSTOMER_SELECT_PLACEHOLDER,
} from "../../shared/customerSelectUtils";

export default function OrderDialog({
    open,
    editingIndex,
    currentOrder,
    orderErrors,
    performanceEntryErrors,
    materialEntryErrors,
    customerOptions,
    loadingCustomers,
    selectedCustomerOption,
    onCustomerChange,
    getCustomerOptionLabel,
    isCustomerOptionEqualToValue,
    onOrderFieldChange,
    onOpenPerformanceDialog,
    onEditPerformance,
    onRemovePerformance,
    onOpenMaterialDialog,
    onEditMaterial,
    onRemoveMaterial,
    onClose,
    onSave,
    savingOrder,
}) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle>{editingIndex !== null ? "Auftrag bearbeiten" : "Neuer Auftrag"}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <Autocomplete
                        options={customerOptions}
                        loading={loadingCustomers}
                        value={selectedCustomerOption}
                        onChange={onCustomerChange}
                        getOptionLabel={getCustomerOptionLabel}
                        isOptionEqualToValue={isCustomerOptionEqualToValue}
                        loadingText={CUSTOMER_SELECT_LOADING}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={CUSTOMER_SELECT_LABEL}
                                placeholder={CUSTOMER_SELECT_PLACEHOLDER}
                                error={Boolean(orderErrors.customerId)}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loadingCustomers ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                helperText={
                                    orderErrors.customerId ||
                                    (!loadingCustomers && customerOptions.length === 0
                                        ? CUSTOMER_SELECT_EMPTY_HELPER
                                        : undefined)
                                }
                            />
                        )}
                        noOptionsText={CUSTOMER_SELECT_NO_OPTIONS}
                        fullWidth
                        disabled={loadingCustomers && customerOptions.length === 0}
                    />
                    <TextField
                        label="Beschreibung"
                        value={currentOrder.title}
                        onChange={(event) => onOrderFieldChange("title", event.target.value)}
                        fullWidth
                        error={Boolean(orderErrors.title)}
                        helperText={orderErrors.title || ""}
                    />

                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                        Leistungen
                    </Typography>
                    <List dense>
                        {currentOrder.performanceDays.map((perf, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <Box>
                                        {performanceEntryErrors[index] &&
                                            Object.keys(performanceEntryErrors[index]).length > 0 && (
                                                <Tooltip title={Object.values(performanceEntryErrors[index]).join("; ")}> 
                                                    <Chip
                                                        label="Fehler"
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        sx={{ mr: 1 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        <IconButton edge="end" onClick={() => onEditPerformance(index)} sx={{ mr: 1 }} aria-label="Bearbeiten">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => onRemovePerformance(index)} aria-label="Löschen">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <ListItemText
                                    primary={`${perf.date || "?"} – ${perf.articleName || "?"} (${perf.amount ?? "?"})`}
                                    secondary={`von ${perf.start || "?"} bis ${perf.end || "?"}`}
                                />
                            </ListItem>
                        ))}
                        {currentOrder.performanceDays.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="Noch keine Leistungen erfasst."
                                    primaryTypographyProps={{ color: "text.secondary" }}
                                />
                            </ListItem>
                        )}
                    </List>

                    <Button
                        variant="contained"
                        onClick={onOpenPerformanceDialog}
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, alignSelf: "flex-start" }}
                    >
                        Leistung hinzufügen
                    </Button>

                    <Typography variant="subtitle2" sx={{ mt: 2 }}>
                        Materialien
                    </Typography>
                    <List dense>
                        {currentOrder.materials.map((mat, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <Box>
                                        {materialEntryErrors[index] &&
                                            Object.keys(materialEntryErrors[index]).length > 0 && (
                                                <Tooltip title={Object.values(materialEntryErrors[index]).join("; ")}> 
                                                    <Chip
                                                        label="Fehler"
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        sx={{ mr: 1 }}
                                                    />
                                                </Tooltip>
                                            )}
                                        <IconButton edge="end" onClick={() => onEditMaterial(index)} sx={{ mr: 1 }} aria-label="Bearbeiten">
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => onRemoveMaterial(index)} aria-label="Löschen">
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                }
                            >
                                <ListItemText primary={`${mat.articleName} (${mat.amount})`} />
                            </ListItem>
                        ))}
                        {currentOrder.materials.length === 0 && (
                            <ListItem>
                                <ListItemText
                                    primary="Noch keine Materialien erfasst."
                                    primaryTypographyProps={{ color: "text.secondary" }}
                                />
                            </ListItem>
                        )}
                    </List>

                    <Button
                        variant="contained"
                        onClick={onOpenMaterialDialog}
                        sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, alignSelf: "flex-start" }}
                    >
                        Material hinzufügen
                    </Button>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} color="inherit">
                    Abbrechen
                </Button>
                <Button
                    onClick={onSave}
                    variant="contained"
                    sx={{ bgcolor: "orange", "&:hover": { bgcolor: "#cc7a00" }, borderRadius: 2, color: "#000" }}
                    disabled={savingOrder}
                >
                    {savingOrder ? "Speichern..." : "Speichern"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
